import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Alert from '@pelckmans/elementis/components/alert';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Link from '@pelckmans/elementis/components/link';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { fetchGroups, Group } from '../../../../api/groups';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import useLicenses, { LICENSE_VALIDATION_TYPES } from '../../../../hooks/useLicenses';
import { useRoutes } from '../../../../hooks/useRoutes';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ContentBody from '../../../../layout/content/ContentBody';
import ContentHeader from '../../../../layout/content/ContentHeader';
import AggregatedModuleAlerts from '../../../../shared-components/alerts/AggregatedModuleAlerts';
import ModuleContext from '../../../material/_contexts/ModuleContext';
import SuperModuleBreadcrumb from '../../components/breadcrumbs/SuperModuleBreadcrumb';
import AllAssignments from './components/Assignments';

const BASE = 'PAGES.ASSIGNMENTS.TEACHER';

type TabId = typeof TABS[keyof typeof TABS];

const TABS = {
  PLANNING: 'PLANNING',
  ARCHIVE: 'ARCHIVE',
};

function Assignments() {
  const { module, allModules } = useContext(ModuleContext);
  const { t } = useTranslation();
  const { getRoute } = useRoutes();
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserLicenseValidationType } = useLicenses();

  const allModulesLicensed = allModules.every(
    module => getUserLicenseValidationType(module) === LICENSE_VALIDATION_TYPES.LICENSED,
  );

  const { buildAbsolutePath } = useRoutes();
  const params = {
    subjectSlug: module?.subjectSlug || '',
    methodSlug: module?.methodSlug || '',
    moduleSlug: module?.slug || '',
  };

  const selectedTab: TabId | undefined = (Object.values(TABS) as TabId[]).find(tab => {
    const route = tab === TABS.PLANNING ? 'MATERIAL.ASSIGNMENTS.PLANNING' : 'MATERIAL.ASSIGNMENTS.ARCHIVE';
    return location.pathname.includes(getRoute(route));
  });

  const onTabChange = (tab: string) => {
    if (tab === selectedTab) return;

    const route = tab === TABS.PLANNING ? 'MATERIAL.ASSIGNMENTS.PLANNING' : 'MATERIAL.ASSIGNMENTS.ARCHIVE';
    navigate(getRoute(route));
  };

  return (
    <Page>
      <Tabs selectedTab={selectedTab} onTabChange={onTabChange}>
        <ContentHeader
          cover={
            <Page.Header
              breadcrumbs={
                <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                  <SuperModuleBreadcrumb />
                </Breadcrumbs>
              }
              headerText={t('PAGES.ASSIGNMENTS.PAGE_TITLE')}
              tabs={
                <Tabs.TabList aria-label={t(`${BASE}.TAB_LIST.A11Y_LABEL`)}>
                  <Tabs.Tab id={TABS.PLANNING}>{t(`${BASE}.TAB_LIST.TABS.PLANNING`)}</Tabs.Tab>
                  <Tabs.Tab id={TABS.ARCHIVE}>{t(`${BASE}.TAB_LIST.TABS.ARCHIVED`)}</Tabs.Tab>
                </Tabs.TabList>
              }
            />
          }
        />
        <ContentBody
          alerts={
            allModulesLicensed ? (
              <Alert prominence="info">
                {t(`${BASE}.ALERTS.ADD_ASSIGNMENT.TEXT_1`)}
                <Link to={buildAbsolutePath(['MATERIAL'], params)}>{t(`${BASE}.ALERTS.ADD_ASSIGNMENT.LINK`)}</Link>
                {t(`${BASE}.ALERTS.ADD_ASSIGNMENT.TEXT_2`)}
              </Alert>
            ) : (
              <AggregatedModuleAlerts selectedModules={allModules} showLicenseAlertsOnly />
            )
          }
          scrollableTable
        >
          <Tabs.TabsContent>
            {(Object.values(TABS) as TabId[]).map(tabId => {
              return (
                <Tabs.TabPanel key={tabId} id={tabId}>
                  <Outlet />
                </Tabs.TabPanel>
              );
            })}
          </Tabs.TabsContent>
        </ContentBody>
      </Tabs>
    </Page>
  );
}

export function TeacherAssignmentTabs({ tab }: { tab: string }) {
  const { currentSchoolYear } = useSchoolYear();
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const handleError = useThrowAsyncError();
  const { t } = useTranslation();

  useDocumentTitle(
    tab === TABS.ARCHIVE
      ? t('PAGES.ASSIGNMENTS.TEACHER.ARCHIVED.DOCUMENT_TITLE')
      : t('PAGES.ASSIGNMENTS.TEACHER.PLANNING.DOCUMENT_TITLE'),
  );
  useEffect(() => {
    const getGroupsOfSchoolYear = async () => {
      setIsLoadingGroups(true);

      const fetchedGroups = await fetchGroups({
        startYear: currentSchoolYear.from.getFullYear(),
        endYear: currentSchoolYear.to.getFullYear(),
      });

      setGroups(fetchedGroups);
      setIsLoadingGroups(false);
    };

    getGroupsOfSchoolYear().catch(handleError);
  }, [handleError, currentSchoolYear]);

  return <AllAssignments groups={groups} isLoadingGroups={isLoadingGroups} archived={tab === TABS.ARCHIVE} />;
}

export default Assignments;
